/* eslint-disable max-lines */
import React, {
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react'
import Button from 'components/Button'
import {ConnStates} from 'api/types/properties'
import {EmbedContext} from 'components/EmbedContext'
import {addAppListener} from '../../middlewares/listener'
import {isOnline as appIsOnline} from 'app'
import ninchatClient from 'api/NinchatClient'
import {online} from 'app/actions'
import {toast} from 'components/Toast'
import {useAppDispatch} from 'hooks'
import {useTranslation} from 'react-i18next'

const TOAST_BUTTON_DELAY_IN_MS = 5000
const TOAST_DISCONNECTED_DELAY_IN_MS = 5000

enum Status {
    ONLINE = 'online',
    CONNECTING = 'connecting',
    CONNECTED = 'connected',
    INACTIVE = 'inactive',
    DISCONNECTED = 'disconnected',
    OFFLINE = 'offline',
}

const toastButtonMessage = (message: string, text: string, onPress: (() => void)) => (
    <>
        <div>{message}</div>
        <Button color='filled' onPress={onPress} text={text} />
    </>
)

const reconnect = () => ninchatClient.reconnect()

const Connection = () => {
    const [connState, setConnState] = useState<ConnStates | undefined>(ninchatClient.connectionState)
    const [isConnActive, setIsConnActive] = useState<boolean | undefined>(ninchatClient.connectionActive)
    const [isOnline, setIsOnline] = useState<boolean>(appIsOnline)
    const [status, setStatus] = useState<Status>()
    const embed = useContext(EmbedContext)
    const isEmbedQueueChannel = embed?.variant === 'queue' || embed?.variant === 'channel'

    const hasConnected = useRef(false)
    const statusTimer = useRef<ReturnType<typeof setTimeout>>()

    const dispatch = useAppDispatch()
    const {t} = useTranslation()

    useEffect(() => {
        const unsubscribes: (() => void)[] = []

        unsubscribes.push(dispatch(addAppListener({
            actionCreator: online,
            effect: (action) => {
                setIsOnline(action.payload)

                if (action.payload) {
                    reconnect()
                }
            },
        })))
        unsubscribes.push(dispatch(addAppListener({
            actionCreator: ninchatClient.connStateAction,
            effect: (action) => setConnState(action.payload),
        })))
        unsubscribes.push(dispatch(addAppListener({
            actionCreator: ninchatClient.connActiveAction,
            effect: (action) => setIsConnActive(action.payload),
        })))
        unsubscribes.push(dispatch(addAppListener({
            actionCreator: ninchatClient.closeAction,
            effect: () => {
                statusTimer.current = clearTimeout(statusTimer.current) as typeof statusTimer.current
                hasConnected.current = false
                setConnState(undefined)
                setIsConnActive(undefined)
            },
        })))
        return () => {
            statusTimer.current = clearTimeout(statusTimer.current) as typeof statusTimer.current
            unsubscribes.forEach((unsubscribe) => unsubscribe())
        }
    }, [])

    useEffect(() => {
        if (isOnline === false) {
            setStatus(Status.OFFLINE)
            return
        }

        if (isConnActive === false && connState === ConnStates.CONNECTED) {
            setStatus(Status.INACTIVE)
            return
        }

        switch (connState) {
            case ConnStates.CONNECTING:
                setStatus(Status.CONNECTING)
                break
            case ConnStates.CONNECTED:
                setStatus(Status.CONNECTED)
                break
            case ConnStates.DISCONNECTED:
                setStatus(Status.DISCONNECTED)
                break
            default:
                setStatus(hasConnected.current ? Status.DISCONNECTED : Status.ONLINE)
        }
    }, [isOnline, connState, isConnActive])

    useEffect(() => {
        statusTimer.current = clearTimeout(statusTimer.current) as typeof statusTimer.current

        switch (status) {
            case Status.ONLINE:
                if (isEmbedQueueChannel) {
                    embed?.setEmbedState((prevState) => ({
                        ...prevState,
                        connectionError: null,
                    }))
                } else {
                    toast.dismiss('status')
                }
                break
            case Status.OFFLINE:
                if (isEmbedQueueChannel) {
                    embed?.setEmbedState((prevState) => ({
                        ...prevState,
                        connectionError: {
                            message: t('Trying to reconnect...'),
                            title: t('Connection lost'),
                        },
                    }))
                } else {
                    toast.error(t('Offline'), {
                        duration: Infinity,
                        id: 'status',
                    })
                }
                break
            default:
        }

        if (status === Status.CONNECTED && !hasConnected.current) {
            hasConnected.current = true
            return
        }

        if (!hasConnected.current) {
            return
        }

        switch (status) {
            case Status.CONNECTING:
                if (isEmbedQueueChannel) {
                    embed?.setEmbedState((prevState) => ({
                        ...prevState,
                        connectionError: {
                            message: t('Trying to reconnect...'),
                            title: t('Connection lost'),
                        },
                    }))
                } else {
                    toast.loading(t('Connection lost, trying to reconnect...'), {
                        duration: Infinity,
                        id: 'status',
                    })
                }
                break
            case Status.CONNECTED:
                if (isEmbedQueueChannel) {
                    embed?.setEmbedState((prevState) => ({
                        ...prevState,
                        connectionError: null,
                    }))
                } else {
                    toast.success(t('Connected'), {
                        duration: 2000,
                        id: 'status',
                    })
                }
                break
            case Status.INACTIVE:
                if (isEmbedQueueChannel) {
                    embed?.setEmbedState((prevState) => ({
                        ...prevState,
                        connectionError: {
                            message: t('Trying to reconnect...'),
                            title: t('Connection lost'),
                        },
                    }))
                } else {
                    toast.error(t('Connection inactive'), {
                        duration: Infinity,
                        id: 'status',
                    })
                }
                break
            case Status.DISCONNECTED: {
                statusTimer.current = setTimeout(() => {
                    if (isEmbedQueueChannel) {
                        embed?.setEmbedState((prevState) => ({
                            ...prevState,
                            connectionError: {
                                message: t('Trying to reconnect...'),
                                title: t('Connection lost'),
                            },
                        }))
                    } else {
                        toast.error(t('Connection lost'), {
                            duration: Infinity,
                            id: 'status',
                        })
                    }

                    statusTimer.current = setTimeout(() => {
                        if (isEmbedQueueChannel) {
                            embed?.setEmbedState((prevState) => ({
                                ...prevState,
                                connectionError: {
                                    message: t('Trying to reconnect...'),
                                    title: t('Connection lost'),
                                },
                            }))
                        } else {
                            toast.error(toastButtonMessage(
                                t('Connection lost'),
                                t('Reconnect now'),
                                reconnect,
                            ), {
                                duration: Infinity,
                                id: 'status',
                            })
                        }
                    }, TOAST_BUTTON_DELAY_IN_MS)
                }, TOAST_DISCONNECTED_DELAY_IN_MS)
                break
            }
            default:
        }
    }, [status])

    return null
}

export default Connection
